<!--
    Popup d'ENVOI PERSONNALISÉ d'un devis.

    Ouverte depuis le menu « Envoyer » (datatable et kanban), option « Envoi
    personnalisé ». L'envoi simple ne passe PAS par ici : il garde son appel direct
    à /back-office/devis/{orderId}/sent-quote, tout comme le drag & drop du kanban.

    Le formulaire ne pilote que le CORPS du message : destinataire, objet, PDF joint
    et expéditeur sont décidés côté serveur à partir de la commande. Ce qui est
    affiché ici (client, sujet, pièce jointe) n'est qu'un rappel de ce que le
    serveur va envoyer, jamais une donnée qui voyage dans la requête.
-->
<template>
    <CustomDialog v-model="isVisible" width="720px" :default-title="title" @cancel="close">
        <template #content>
            <div class="ppv2-px-2 ppv2-space-y-5">
                <!-- Sujet (imposé par le serveur, non modifiable) -->
                <div class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-text-sm ppv2-text-black ppv2-text-opacity-70">
                    <span class="poppinsMedium">{{ $t('quotes.send_modal.subject_label') }}</span>
                    <span class="poppinsRegular">{{ subject }}</span>
                </div>

                <!-- Contenu personnalisé (CKEditor via BaseTextarea) -->
                <BaseTextarea
                    v-model="content"
                    :key="editorKey"
                    :label="$t('quotes.send_modal.content_label')"
                    :placeholder="$t('quotes.send_modal.content_placeholder')"
                    :rows="8"
                    :disabled="sending"
                    :error="error"
                />

                <!-- Pièce jointe : le PDF réellement joint par le serveur -->
                <div class="ppv2-border ppv2-border-black ppv2-border-opacity-10 ppv2-rounded-xl ppv2-p-4">
                    <div class="ppv2-text-xs ppv2-text-black ppv2-text-opacity-50 poppinsMedium ppv2-mb-2">
                        {{ $t('quotes.send_modal.attachment_label') }}
                    </div>
                    <a
                        :href="attachmentUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-text-sm ppv2-text-[#7D2DBD] hover:ppv2-underline"
                    >
                        <span
                            class="ppv2-w-9 ppv2-h-9 ppv2-flex ppv2-items-center ppv2-justify-center ppv2-rounded-lg ppv2-bg-black ppv2-bg-opacity-5"
                        >
                            <FileText :size="18" />
                        </span>
                        <span class="poppinsMedium">{{ attachmentName }}</span>
                    </a>
                </div>
            </div>
        </template>

        <template #actions>
            <BaseButton
                variant="outline"
                :custom-height="43"
                custom-class="!ppv2-min-h-[40px] !ppv2-h-[45px] !ppv2-min-w-[135px] ppv2-bg-white dark:ppv2-bg-dark-popup"
                :disabled="sending"
                @click="close"
            >
                {{ $t('common.cancel') }}
            </BaseButton>
            <BaseButton
                variant="purple"
                :custom-height="43"
                custom-class="!ppv2-min-h-[40px] !ppv2-h-[45px] !ppv2-min-w-[135px]"
                :loading="sending"
                :disabled="sending"
                @click="send"
            >
                {{ $t('quotes.send_modal.send') }}
            </BaseButton>
        </template>
    </CustomDialog>
</template>

<script>
import CustomDialog from '../Shared/CustomDialog.vue'
import BaseButton from '../Shared/BaseButton.vue'
import BaseTextarea from '../Shared/BaseTextarea.vue'
import { FileText } from 'lucide-vue'

export default {
    name: 'DevisSendModal',
    components: { CustomDialog, BaseButton, BaseTextarea, FileText },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        quote: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            content: '',
            sending: false,
            error: '',
            // BaseTextarea instancie CKEditor dans mounted() : la clé force une
            // ré-instanciation propre à chaque ouverture, sinon l'éditeur garde le
            // contenu du devis précédent.
            editorKey: 0,
        }
    },
    computed: {
        isVisible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        clientName() {
            const user = this.quote?.user
            if (!user) return this.$t('quotes.card.deleted_client')
            const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
            return name || user.email || this.$t('quotes.card.deleted_client')
        },
        title() {
            return this.$t('quotes.send_modal.title', { client: this.clientName })
        },
        quoteNumber() {
            return this.quote?.serial_number || this.quote?.order_id || ''
        },
        subject() {
            return this.$t('quotes.send_modal.subject_value', { number: this.quoteNumber })
        },
        attachmentName() {
            return `devis-#${this.quote?.order_id ?? ''}.pdf`
        },
        // Même génération que le PDF joint au mail (InvoiceService::generateDevis) :
        // le commercial relit exactement le document que le client recevra.
        attachmentUrl() {
            return this.quote?.order_id ? `/back-office/devis/${this.quote.order_id}` : '#'
        },
    },
    watch: {
        value(val) {
            if (val) {
                this.content = ''
                this.error = ''
                this.editorKey++
            }
        },
    },
    methods: {
        close() {
            if (this.sending) return
            this.isVisible = false
        },
        hasContent() {
            // Le HTML de l'éditeur est « vide » dès qu'il ne reste que du balisage.
            const text = this.content
                .replace(/<[^>]*>/g, ' ')
                .replace(/&nbsp;/g, ' ')
                .trim()
            return text.length > 0
        },
        async send() {
            if (this.sending) return

            if (!this.hasContent()) {
                this.error = this.$t('quotes.send_modal.content_required')
                return
            }

            if (!this.quote?.order_id) return

            this.sending = true
            this.error = ''
            try {
                await this.axios.post(`/back-office/devis/${this.quote.order_id}/send-custom`, {
                    content: this.content,
                })
                this.$toast.success(this.$t('quotes.card.toasts.sent'))
                this.isVisible = false
                this.$emit('sent')
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('quotes.card.toasts.error'))
            } finally {
                this.sending = false
            }
        },
    },
}
</script>
